import { LegalPage, type LegalSection } from "./LegalPage";

/**
 * Privacy Policy — editorial boilerplate that aligns with:
 *   - UAE Federal Decree-Law No. 45 of 2021 (Personal Data Protection Law)
 *   - DLD/RERA record-keeping obligations for licensed brokers
 *   - Bitrix24 (our CRM) and standard hosting/analytics processors
 *
 * The copy is written to be informative and non-evasive; specifics
 * (data retention windows, named sub-processors, DSR turnaround) are
 * intentionally generous defaults that the client's legal counsel can
 * tighten before launch without changing the structure.
 */

const SECTIONS: LegalSection[] = [
  {
    id: "who-we-are",
    heading: "Who We Are",
    body: (
      <>
        <p>
          Hive Homes Real Estate Brokerage L.L.C. ("Hive Homes", "we", "us"
          or "our") is a real estate brokerage licensed by the Dubai Land
          Department and registered with the Real Estate Regulatory Agency
          ("RERA"). Our registered office is in Jumeirah Village Circle,
          Dubai, United Arab Emirates.
        </p>
        <p>
          We are the controller of personal data collected through this
          website, our property listings, our CRM, and any direct
          interaction you have with our advisors. This Privacy Policy
          explains what data we collect, how we use it, who we share it
          with, and the choices and rights available to you under UAE
          Federal Decree-Law No. 45 of 2021 on the Protection of Personal
          Data (the "PDPL").
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    heading: "What Information We Collect",
    body: (
      <>
        <p>We collect the following categories of personal data:</p>
        <ul>
          <li>
            <strong>Identity and contact details</strong> — your first and
            last name, email address, mobile or WhatsApp number, and the
            country you are enquiring from. This is information you give us
            directly through our enquiry forms, by phone, by WhatsApp, or
            in person.
          </li>
          <li>
            <strong>Property requirements</strong> — what you are looking
            to buy, sell, rent, or let; preferred communities; budget;
            handover timing for off-plan; and any notes you share with our
            advisors about your situation.
          </li>
          <li>
            <strong>Transactional and compliance information</strong> — for
            clients moving to instruction, this includes copies of
            identification documents, proof of address, and source-of-funds
            documentation that RERA, DLD, and the UAE's anti-money
            laundering regulations require us to verify and keep on file.
          </li>
          <li>
            <strong>Website usage data</strong> — IP address, device and
            browser type, pages viewed, properties shortlisted, and the
            referring source. We collect this through privacy-respecting
            analytics and our own server logs.
          </li>
          <li>
            <strong>Communications</strong> — emails, WhatsApp threads, and
            call notes between you and our team, retained so we can give
            you a consistent service and meet RERA record-keeping
            obligations.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    heading: "How We Use Your Information",
    body: (
      <>
        <p>We use personal data only for purposes that are lawful, fair, and proportionate, including:</p>
        <ul>
          <li>
            Responding to your enquiry, arranging viewings, and presenting
            properties that match your brief.
          </li>
          <li>
            Negotiating offers, drafting Forms F / A / B / I as required by
            RERA, and coordinating with developers, conveyancers, mortgage
            advisors, and the Dubai Land Department on your behalf.
          </li>
          <li>
            Meeting our obligations under UAE law, including
            know-your-customer (KYC) checks, anti-money laundering
            screening, sanctions screening, and RERA record-keeping.
          </li>
          <li>
            Sending market intelligence, community guides, and off-market
            opportunities — only if you opt in, and only as often as our
            published cadence (typically once a month).
          </li>
          <li>
            Maintaining the security of our website and CRM, debugging,
            preventing fraud, and improving the relevance of our content.
          </li>
        </ul>
        <p>
          We do not sell your personal data, and we do not share it for
          third-party marketing purposes.
        </p>
      </>
    ),
  },
  {
    id: "legal-bases",
    heading: "Our Legal Bases for Processing",
    body: (
      <>
        <p>
          Under the PDPL we rely on the following bases to process your
          personal data:
        </p>
        <ul>
          <li>
            <strong>Your consent</strong>, for marketing communications and
            for any non-essential analytics or cookies.
          </li>
          <li>
            <strong>Performance of a contract</strong> or steps taken at
            your request prior to entering into one — for example, when
            you ask us to arrange a viewing or represent you in a
            transaction.
          </li>
          <li>
            <strong>Legal obligation</strong>, including RERA, AML/CFT, and
            tax-related record-keeping requirements that apply to licensed
            brokers in the UAE.
          </li>
          <li>
            <strong>Our legitimate interests</strong>, such as keeping our
            website secure, understanding how our service is being used,
            and following up on enquiries where a relationship has already
            begun.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "who-we-share-with",
    heading: "Who We Share Information With",
    body: (
      <>
        <p>
          We share personal data only where it is necessary and only with
          parties that handle it under appropriate safeguards:
        </p>
        <ul>
          <li>
            <strong>Our CRM provider, Bitrix24</strong>, which we use to
            manage enquiries, properties, and client relationships. Bitrix24
            operates under its own data protection commitments and is
            contractually bound to process data only on our instructions.
          </li>
          <li>
            <strong>Developers, conveyancers, mortgage advisors and the
            Dubai Land Department</strong>, where the introduction or
            transaction you have engaged us for requires it.
          </li>
          <li>
            <strong>Hosting, email and analytics providers</strong> that
            keep this website and our communications running. Where any of
            these providers are located outside the UAE, we ensure your
            data is protected by appropriate safeguards before transferring
            it.
          </li>
          <li>
            <strong>Regulators and law enforcement</strong>, where we are
            required to do so by UAE law or by an order from a court of
            competent jurisdiction.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    heading: "How Long We Keep It",
    body: (
      <>
        <p>
          We keep personal data only for as long as it serves the purpose
          it was collected for. As a guide:
        </p>
        <ul>
          <li>
            Enquiry data where no transaction follows is kept for up to
            36 months and then deleted or fully anonymised.
          </li>
          <li>
            Transactional and compliance records, including KYC, AML, and
            agency-agreement files, are retained for the minimum period
            required by RERA and UAE AML regulations (currently five years
            from the end of the relationship, longer if litigation is
            reasonably anticipated).
          </li>
          <li>
            Marketing consent records are kept for the duration of your
            subscription and for two years after you unsubscribe, so we
            can prove the original consent if asked.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "your-rights",
    heading: "Your Rights",
    body: (
      <>
        <p>
          The PDPL gives you specific rights over your personal data. You
          can ask us to:
        </p>
        <ul>
          <li>Confirm whether we hold personal data about you and provide a copy.</li>
          <li>Correct anything that is inaccurate or out of date.</li>
          <li>Delete data where we no longer have a lawful basis to keep it.</li>
          <li>
            Restrict or object to certain processing, including marketing.
          </li>
          <li>
            Withdraw your consent at any time, where consent was the basis
            of processing.
          </li>
          <li>
            Receive your data in a structured, commonly used format and ask
            us to transmit it to another controller, where technically
            feasible.
          </li>
        </ul>
        <p>
          To exercise any of these rights, email us at the address below
          and our Data Protection point of contact will respond within two
          business days, and complete the request within thirty days as
          required by the PDPL.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "Cookies and Tracking",
    body: (
      <>
        <p>
          This website uses a small number of cookies and similar
          technologies. Essential cookies keep your session, your
          shortlist, and your form progress working — these cannot be
          switched off. Optional analytics cookies help us understand which
          communities and articles are useful, and are only set if you
          consent.
        </p>
        <p>
          You can change your cookie preferences in your browser at any
          time. Disabling essential cookies will affect features such as
          property shortlisting and saved searches.
        </p>
      </>
    ),
  },
  {
    id: "security",
    heading: "Security",
    body: (
      <>
        <p>
          We take the security of your personal data seriously. Access to
          our CRM is restricted to advisors and operations staff who need
          it for their work, protected by individual accounts and
          multi-factor authentication. Our website is served over HTTPS,
          and sensitive transactional documents are handled through secure
          channels with our developer and conveyancing partners.
        </p>
        <p>
          No system is perfectly secure. If we ever become aware of a
          personal-data incident that is likely to result in a high risk to
          your rights, we will notify the UAE Data Office and the affected
          individuals as required by the PDPL.
        </p>
      </>
    ),
  },
  {
    id: "updates",
    heading: "Changes to This Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time to reflect
        changes in the way we operate or in applicable law. When we make a
        material change, we will update the "last updated" date at the top
        of the page and, where appropriate, notify you directly.
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Privacy Policy"
      title="Your Data, Handled with Care."
      intro="This policy explains what personal data Hive Homes collects, why we collect it, who we share it with, and the rights you have under UAE law. We have written it in plain English; if anything is unclear, please ask."
      lastUpdated="May 2026"
      sections={SECTIONS}
      seoTitle="Privacy Policy | Hive Homes Dubai"
      seoDescription="How Hive Homes Dubai collects, uses and protects your personal data under UAE Federal Decree-Law No. 45 of 2021 (PDPL) and RERA broker requirements."
    />
  );
}
